import React from 'react';
import '../styles/PrintableConsignment.css';

const PrintableConsignment = ({ data, type = 'LT' }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };

  return (
    <div className="printable-consignment">
      {/* Header */}
      <div className="print-header">
        <div className="company-info">
          <h1>LOGISTICS & TRANSPORT</h1>
          <p>Land Transport Management System</p>
        </div>
        <div className="consignment-title">
          <h2>{type === 'LT' ? 'LAND TRANSPORT' : 'DOMESTIC'} CONSIGNMENT</h2>
          <div className="consignment-number">{data.ConsignmentNo || 'N/A'}</div>
        </div>
      </div>

      {/* Basic Info Bar */}
      <div className="info-bar">
        <div className="info-item">
          <span className="label">Date & Time:</span>
          <span className="value">{formatDate(data.ConsignmentDate)}</span>
        </div>
        <div className="info-item">
          <span className="label">Type:</span>
          <span className="value">{type === 'LT' ? 'Land Transport' : 'Domestic'}</span>
        </div>
        {type === 'LT' && data.Load && (
          <div className="info-item">
            <span className="label">Load:</span>
            <span className="value">{data.Load}</span>
          </div>
        )}
      </div>

      {/* Route Section */}
      <div className="print-section route-section">
        <h3>ROUTE INFORMATION</h3>
        <div className="route-display">
          <div className="route-point">
            <div className="route-label">ORIGIN</div>
            <div className="route-city">{data.OriginCity || 'Not specified'}</div>
          </div>
          <div className="route-arrow">→</div>
          <div className="route-point">
            <div className="route-label">DESTINATION</div>
            <div className="route-city">{data.DestinationCity || 'Not specified'}</div>
          </div>
        </div>
      </div>

      {/* Shipper & Consignee */}
      <div className="print-section parties-section">
        <div className="party-box">
          <h3>SHIPPER / CONSIGNOR</h3>
          <table className="party-table">
            <tbody>
              <tr>
                <td className="field-label">Customer Code:</td>
                <td className="field-value">{data.ShipperCode || '-'}</td>
              </tr>
              <tr>
                <td className="field-label">Name:</td>
                <td className="field-value"><strong>{data.ShipperName || '-'}</strong></td>
              </tr>
              <tr>
                <td className="field-label">Contact Person:</td>
                <td className="field-value">{data.ShipperContactPerson || '-'}</td>
              </tr>
              <tr>
                <td className="field-label">Phone:</td>
                <td className="field-value">{data.ShipperPhone || '-'}</td>
              </tr>
              {data.ShipperWhatsApp && (
                <tr>
                  <td className="field-label">WhatsApp:</td>
                  <td className="field-value">{data.ShipperWhatsApp}</td>
                </tr>
              )}
              <tr>
                <td className="field-label">Address:</td>
                <td className="field-value">
                  {[
                    data.ShipperFlatBuilding,
                    data.ShipperStreet,
                    data.ShipperCity
                  ].filter(Boolean).join(', ') || '-'}
                </td>
              </tr>
              {type === 'LT' && (
                <>
                  <tr>
                    <td className="field-label">Location:</td>
                    <td className="field-value">
                      {[
                        data.ShipperCountry,
                        data.ShipperState,
                        data.ShipperCityName
                      ].filter(Boolean).join(', ') || '-'}
                    </td>
                  </tr>
                  {data.ShipperCustomerIDNo && (
                    <tr>
                      <td className="field-label">Customer ID:</td>
                      <td className="field-value">{data.ShipperCustomerIDNo}</td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>

        <div className="party-box">
          <h3>CONSIGNEE / RECEIVER</h3>
          <table className="party-table">
            <tbody>
              <tr>
                <td className="field-label">Customer Code:</td>
                <td className="field-value">{data.ConsigneeCode || '-'}</td>
              </tr>
              <tr>
                <td className="field-label">Name:</td>
                <td className="field-value"><strong>{data.ConsigneeName || '-'}</strong></td>
              </tr>
              <tr>
                <td className="field-label">Contact Person:</td>
                <td className="field-value">{data.ConsigneeContactPerson || '-'}</td>
              </tr>
              <tr>
                <td className="field-label">Phone:</td>
                <td className="field-value">{data.ConsigneePhone || '-'}</td>
              </tr>
              {data.ConsigneeWhatsApp && (
                <tr>
                  <td className="field-label">WhatsApp:</td>
                  <td className="field-value">{data.ConsigneeWhatsApp}</td>
                </tr>
              )}
              <tr>
                <td className="field-label">Address:</td>
                <td className="field-value">
                  {[
                    data.ConsigneeFlatBuilding,
                    data.ConsigneeStreet,
                    data.ConsigneeCity
                  ].filter(Boolean).join(', ') || '-'}
                </td>
              </tr>
              {type === 'LT' && (
                <tr>
                  <td className="field-label">Location:</td>
                  <td className="field-value">
                    {[
                      data.ConsigneeCountry,
                      data.ConsigneeState,
                      data.ConsigneeCityName
                    ].filter(Boolean).join(', ') || '-'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Package Details */}
      <div className="print-section package-section">
        <h3>PACKAGE DETAILS</h3>
        <table className="details-table">
          <tbody>
            <tr>
              <td className="field-label">Description:</td>
              <td className="field-value" colSpan="3">{data.Description || '-'}</td>
            </tr>
            <tr>
              <td className="field-label">Weight:</td>
              <td className="field-value">{data.Weight ? `${data.Weight} kg` : '-'}</td>
              <td className="field-label">Pieces:</td>
              <td className="field-value">{data.Pieces || '-'}</td>
            </tr>
            <tr>
              <td className="field-label">No. of Packages:</td>
              <td className="field-value">{data.NoOfPkgs || '-'}</td>
              <td className="field-label">Invoice Value:</td>
              <td className="field-value">{data.InvoiceValue ? `${data.InvoiceValue}` : '-'}</td>
            </tr>
            {type === 'LT' && (
              <>
                <tr>
                  <td className="field-label">Volume:</td>
                  <td className="field-value">{data.Volume ? `${data.Volume} m³` : '-'}</td>
                  <td className="field-label">Volume Weight:</td>
                  <td className="field-value">{data.VolumeWeight ? `${data.VolumeWeight} kg` : '-'}</td>
                </tr>
                <tr>
                  <td className="field-label">Product Type:</td>
                  <td className="field-value">{data.ProductType || '-'}</td>
                  <td className="field-label">Courier Type:</td>
                  <td className="field-value">{data.CourierType || '-'}</td>
                </tr>
              </>
            )}
            <tr>
              <td className="field-label">Payment Mode:</td>
              <td className="field-value">{data.PaymentMode || '-'}</td>
              <td className="field-label">Courier Type:</td>
              <td className="field-value">{data.CourierType || '-'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Transport Details */}
      <div className="print-section transport-section">
        <h3>TRANSPORT & DELIVERY DETAILS</h3>
        <table className="details-table">
          <tbody>
            {type === 'LT' && (
              <>
                <tr>
                  <td className="field-label">Vehicle:</td>
                  <td className="field-value">{data.VehicleNo || '-'}</td>
                  <td className="field-label">Driver:</td>
                  <td className="field-value">{data.DriverName || '-'}</td>
                </tr>
                <tr>
                  <td className="field-label">Despatch Date:</td>
                  <td className="field-value">{formatShortDate(data.DespatchDate)}</td>
                  <td className="field-label">Load Type:</td>
                  <td className="field-value">{data.Load || '-'}</td>
                </tr>
              </>
            )}
            {type === 'D' && data.DriverName && (
              <tr>
                <td className="field-label">Driver:</td>
                <td className="field-value" colSpan="3">{data.DriverName}</td>
              </tr>
            )}
            <tr>
              <td className="field-label">Collected By:</td>
              <td className="field-value">{data.CollectedByName || '-'}</td>
              <td className="field-label">Received By:</td>
              <td className="field-value">{data.ReceivedByName || '-'}</td>
            </tr>
            <tr>
              <td className="field-label">Status:</td>
              <td className="field-value">{data.CourierStatus || '-'}</td>
              <td className="field-label">Delivery Date:</td>
              <td className="field-value">{formatShortDate(data.DeliveryDate)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Special Instructions (LT only) */}
      {type === 'LT' && (
        <div className="print-section instructions-section">
          <h3>SPECIAL INSTRUCTIONS</h3>
          <div className="instructions-grid">
            {data.NCND && <div className="instruction-badge">✓ NCND</div>}
            {data.DDCopyBack && <div className="instruction-badge">✓ DD Copy Back</div>}
            {data.CashOnly && <div className="instruction-badge">✓ Cash Only</div>}
            {data.ChequeCash && <div className="instruction-badge">✓ Cheque/Cash</div>}
            {data.CollectMaterial && <div className="instruction-badge">✓ Collect Material</div>}
            {data.Transit && <div className="instruction-badge">✓ Transit</div>}
            {data.SeparateDocs && <div className="instruction-badge">✓ Separate Documents</div>}
          </div>
          {data.MaterialCost && (
            <div className="material-cost">Material Cost: {data.MaterialCost}</div>
          )}
          {data.Reference && (
            <div className="reference">Reference: {data.Reference}</div>
          )}
        </div>
      )}

      {/* Remarks */}
      {data.Remarks && (
        <div className="print-section remarks-section">
          <h3>REMARKS</h3>
          <p>{data.Remarks}</p>
        </div>
      )}

      {/* Signature Section */}
      <div className="print-section signature-section">
        <div className="signature-box">
          <div className="signature-line"></div>
          <div className="signature-label">Shipper Signature</div>
        </div>
        <div className="signature-box">
          <div className="signature-line"></div>
          <div className="signature-label">Driver Signature</div>
        </div>
        <div className="signature-box">
          <div className="signature-line"></div>
          <div className="signature-label">Consignee Signature</div>
        </div>
      </div>

      {/* Footer */}
      <div className="print-footer">
        <p>This is a computer-generated document. No signature required.</p>
        <p>Printed on: {new Date().toLocaleString()}</p>
      </div>
    </div>
  );
};

export default PrintableConsignment;